<script setup>
import { ref, watch, computed } from "vue";
import { useStore } from "vuex";
import pushNextPage from "@/utils/pushNextPage";

const store = useStore();

const isOpen = computed(() => store.state.drawer.isOpen);

const pageReset = () => store.dispatch("pageReset");

const letters = [
  "Email",
  "Survey",
  "Pop Song",
  "Backstory",
  "Travel Plan",
  "Kid's Game",
  "Film Idea",
  "Sales Pitch",
  "Email",
];
const genie = ref();

const beforePageReset = () => {
  pageReset();
  pushNextPage(store.state);
};

watch(isOpen, (newVal) => {
  if (newVal) {
    genie.value?.classList?.add("animate-fade_out_genie");
    setTimeout(() => {
      genie.value?.classList?.remove("animate-fade_out_genie");
    }, 10000);
  } else {
    genie.value?.classList?.add("animate-fade_in_genie");
    setTimeout(() => {
      genie.value?.classList?.remove("animate-fade_in_genie");
    }, 10000);
  }
});
</script>

<template>
  <!-- On phones and tablets it fills the screen above the start box (see MainContent),
    with the genie growing into whatever room the heading leaves -->
  <div
    id="hero"
    class="flex min-h-[calc(100svh-var(--landing-sheet,100svh))] flex-col px-4 md:px-16"
  >
    <div
      class="flex items-center justify-between py-4 sm:py-6 md:py-14 wide:pb-2"
    >
      <img
        src="/logo-letters.svg"
        alt=""
        class="hidden logo-overlap:block p-5 -m-5 cursor-pointer z-30 relative"
        @click="beforePageReset"
      />
      <img
        src="/logo.svg"
        alt=""
        class="logo-overlap:hidden -my-3 -mx-1 cursor-pointer z-30 relative h-11 sm:h-14"
        @click="beforePageReset"
      />
    </div>
    <div
      class="mt-4 flex flex-1 flex-col justify-between sm:mt-8 wide:mt-2 wide:flex-row wide:gap-6 lg:flex-none lg:flex-row"
    >
      <!-- On phones the heading spans the width; on tablets it's a little smaller, sitting
        to the left. Either way it shrinks when the screen is too short to leave the genie room.
        On wide, short screens (open foldables) it takes the left half, beside the genie.
        On computers it fits beside the genie, and it and the genie shrink on short screens -->
      <div
        class="[--hero-font:clamp(40px,min((100vw_-_2rem)/6,(100svh_-_var(--landing-sheet,20rem))*0.15),150px)] sm:[--hero-font:clamp(40px,min((100vw_-_2rem)/7.5,(100svh_-_var(--landing-sheet,20rem))*0.15),150px)] md:[--hero-font:clamp(40px,min((100vw_-_8rem)/7.5,(100svh_-_var(--landing-sheet,20rem))*0.15),150px)] wide:[--hero-font:clamp(40px,min(8.5vw,(100svh_-_var(--landing-sheet,20rem)_-_5rem)/2.5),110px)] lg:[--hero-font:clamp(64px,min((100vw_-_26rem)/6,(100svh_-_497px)/2.52),126px)]"
      >
        <h2
          class="text-[length:var(--hero-font)] font-medium leading-tight text-white lg:leading-normal"
        >
          Make Me A...
        </h2>
        <div
          class="scroll-container h-[calc(var(--hero-font)*1.09)] overflow-hidden lg:h-[calc(var(--hero-font)*1.02)]"
        >
          <h3
            v-for="(i, index) in letters"
            :key="index"
            :class="
              index % 2 !== 0 ? 'text-accentPrimary' : 'text-accentSecondary'
            "
            class="block h-full animate-spin_words text-[length:var(--hero-font)] font-medium leading-none"
          >
            {{ i }}
          </h3>
        </div>
      </div>
      <!-- The genie stands on the white panel below and grows into the room above it,
        keeping a little space under the heading -->
      <div
        class="relative z-10 min-h-[7rem] flex-1 lg:static lg:min-h-0 lg:flex-none"
      >
        <img
          ref="genie"
          class="absolute bottom-0 left-1/2 h-[min(100%_-_1.5rem,22rem)] w-auto -translate-x-1/2 lg:static lg:mt-8 lg:h-[clamp(140px,100svh_-_529px,300px)] lg:translate-x-0"
          src="/genie-gift.svg"
          width="279"
          height="325"
          alt="genie"
        />
      </div>
    </div>
  </div>
</template>
